import React from 'react';

import {useEvent, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory} from 'react-router-dom';

import {PermissionAccess} from 'components/common/PermissionAccess';
import {DropdownMenu} from 'components/ui-kit/DropdownMenu';
import {Modal} from 'components/ui-kit/Modal';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {utilisationGroupChanged} from 'models/documents/create';
import {fetchInvoiceIrnFx} from 'models/documents/invoices/create';
import {UserPermission} from 'types';

import {AffixingTypeButton, AffixingTypesWrapper, ControlsWrapper} from './styled';

export const Controls = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const fetchIrn = useEvent(fetchInvoiceIrnFx);

    const permissions = useStore(permissions$);

    const [showAffixingTypeModal, setShowAffixingTypeModal] = React.useState(false);
    const ModalSelectAffixingType = () => {
        const translate = (key: string) => t('pages.documents.utilisationTypeSelect.' + key);

        const onSelect = (group: boolean) => {
            utilisationGroupChanged(group);
            history.push(`${ROUTE.DOCUMENTS_CREATE_UTILISATION}?group=${group}`);
        };

        return (
            <Modal
                title={translate('question')}
                show={showAffixingTypeModal}
                modalWidth={0}
                onClose={() => setShowAffixingTypeModal(false)}
            >
                <AffixingTypesWrapper>
                    <AffixingTypeButton onClick={() => onSelect(false)}>
                        <img src="/icons/affixing/unitType.webp" alt="" width={51} />
                        {translate('unit')}
                    </AffixingTypeButton>
                    <AffixingTypeButton onClick={() => onSelect(true)}>
                        <img src={'/icons/affixing/groupType.webp'} alt="" width={51} />
                        {translate('group')}
                    </AffixingTypeButton>
                </AffixingTypesWrapper>
            </Modal>
        );
    };

    return (
        <ControlsWrapper>
            <DropdownMenu label={t('pages.documents.documents.create')} width={195} buttonSize="sm">
                <DropdownMenu.List>
                    <PermissionAccess permissions={[UserPermission.DOCUMENT_CREATE_DROPOUT]}>
                        <DropdownMenu.Item
                            onSelect={() => {
                                history.push(ROUTE.DOCUMENTS_CREATE_DROP_OUT);
                            }}
                        >
                            {t('pages.documents.details.DropOut.DropOutId')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess permissions={[UserPermission.DOCUMENT_CREATE_AGGREGATION]}>
                        <DropdownMenu.Item
                            onSelect={() => {
                                history.push(ROUTE.DOCUMENTS_CREATE_AGGREGATION);
                            }}
                        >
                            {t('pages.documents.details.Aggregation.AggregationId')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess permissions={[UserPermission.DOCUMENT_CREATE_COMMISSIONING]}>
                        <DropdownMenu.Item
                            onSelect={() => {
                                history.push(ROUTE.DOCUMENTS_CREATE_COMMISSIONING);
                            }}
                        >
                            {t('pages.documents.details.Introduce.IntroduceId')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess permissions={[UserPermission.DOCUMENT_CREATE_UTILISATION]}>
                        <DropdownMenu.Item onSelect={() => setShowAffixingTypeModal(true)}>
                            {t('pages.documents.details.Utilization.UtilizationId')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess
                        permissions={[permissions.MPL_ISEMT_INVOICE_SHIPMENT_PREPARE]}
                    >
                        <DropdownMenu.Item
                            onSelect={async () => {
                                try {
                                    const getInvoiceId = await fetchIrn();
                                    const setPath = generatePath(
                                        ROUTE.DOCUMENTS_CREATE_INVOICE_ID,
                                        {
                                            invoiceId: getInvoiceId.data,
                                        },
                                    );
                                    history.push(setPath, {from: location.pathname});
                                } catch (error) {
                                    console.log(((error as unknown) as {message: string}).message);
                                }
                            }}
                        >
                            {t('pages.documents.details.UPD.UPDId')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess permissions={[UserPermission.INVOICE_TRANSFER_VIEW]}>
                        <DropdownMenu.Item
                            onSelect={() => history.push(ROUTE.DOCUMENTS_CREATE_MOVING)}
                        >
                            {t('pages.documents.details.Moving.id')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess permissions={[UserPermission.DOCUMENTS_DECLARATION_CREATE]}>
                        <DropdownMenu.Item
                            onSelect={() => {
                                history.push(ROUTE.DOCUMENTS_GDS_CREATE);
                            }}
                        >
                            {t('pages.documents.gds.create')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess permissions={[UserPermission.CONSUMER_RETURN_CREATE]}>
                        <DropdownMenu.Item
                            onSelect={() => {
                                history.push(ROUTE.DOCUMENTS_CREATE_CONSUMER_RETURN);
                            }}
                        >
                            {t('pages.documents.returnGoods.consumerReturn')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                    <PermissionAccess permissions={[PERMISSION_ALIASES.remarking]}>
                        <DropdownMenu.Item
                            onSelect={() => history.push(ROUTE.DOCUMENTS_CREATE_REMARKING)}
                        >
                            {t('pages.documents.remarking.create')}
                        </DropdownMenu.Item>
                    </PermissionAccess>
                </DropdownMenu.List>
            </DropdownMenu>
            <ModalSelectAffixingType />
        </ControlsWrapper>
    );
};
